// Tiny reactive i18n: a dictionary per locale and `t(key, params)`.
// Reading `t()` inside markup re-renders when the locale changes.

import en, { type Key } from './en';
import ru from './ru';

export type Locale = 'en' | 'ru';
export const LOCALES: { id: Locale; label: string }[] = [
  { id: 'en', label: 'EN' },
  { id: 'ru', label: 'RU' },
];

const dicts: Record<Locale, Record<Key, string>> = { en, ru };
const STORAGE_KEY = 'stereo.locale';

function detect(): Locale {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved === 'en' || saved === 'ru') return saved;
  } catch {
    /* storage unavailable */
  }
  const nav = typeof navigator !== 'undefined' ? navigator.language : 'en';
  return nav.toLowerCase().startsWith('ru') ? 'ru' : 'en';
}

const state = $state({ locale: detect() });

export function locale(): Locale {
  return state.locale;
}

export function setLocale(l: Locale) {
  state.locale = l;
  try {
    localStorage.setItem(STORAGE_KEY, l);
  } catch {
    /* ignore */
  }
  if (typeof document !== 'undefined') document.documentElement.lang = l;
}

export function t(key: Key, params?: Record<string, string | number>): string {
  let s = dicts[state.locale][key] ?? en[key] ?? key;
  if (params) for (const [k, v] of Object.entries(params)) s = s.replaceAll(`{${k}}`, String(v));
  return s;
}

/** Formats a duration in ms as a short human string ("~12 s", "~2 min"). */
export function formatDuration(ms: number): string {
  if (ms < 9500) return t('time.seconds', { n: (Math.max(0, ms) / 1000).toFixed(1).replace(/\.0$/, '') });
  const s = Math.round(ms / 1000);
  if (s < 60) return t('time.seconds', { n: s });
  const m = Math.floor(s / 60);
  const r = s % 60;
  return r ? t('time.minSec', { m, s: r }) : t('time.minutes', { n: m });
}
